import { cltAccentForegroundTokensDark, cltAccentForegroundTokensLight, cltAccentTokens, sunkenBackgroundTokens, } from '../tokens';
import { themeModes } from './design-tokens';
import { runtimeAliases } from './generated/tokens.generated';
const VAR_PREFIX = '--loci';
const joinVar = (name) => `${VAR_PREFIX}-${name}`;
/**
 * Builds a CSS variable record from ThemeTokens for use in styling.
 * @param theme - The theme tokens containing color, spacing, and other design values
 * @returns A record of CSS variable names to their corresponding values
 */
export const buildCssVariableRecord = (theme) => {
    const vars = {};
    // Core semantic colors
    vars[joinVar('color-primary')] = theme.colors.primary;
    vars[joinVar('color-primary-foreground')] = theme.colors.primaryForeground;
    vars[joinVar('color-secondary')] = theme.colors.secondary;
    vars[joinVar('color-secondary-foreground')] = theme.colors.secondaryForeground;
    vars[joinVar('color-success')] = theme.colors.success;
    vars[joinVar('color-warning')] = theme.colors.warning;
    vars[joinVar('color-error')] = theme.colors.error;
    vars[joinVar('color-info')] = theme.colors.info;
    vars[joinVar('color-background')] = theme.colors.background;
    vars[joinVar('color-surface')] = theme.colors.surface;
    vars[joinVar('color-surface-alt')] = theme.colors.surfaceAlt;
    vars[joinVar('color-text')] = theme.colors.text;
    vars[joinVar('color-text-muted')] = theme.colors.textMuted;
    if (theme.colors.textSecondary) {
        vars[joinVar('color-text-secondary')] = theme.colors.textSecondary;
    }
    vars[joinVar('color-border')] = theme.colors.border;
    vars[joinVar('color-ring')] = theme.colors.ring;
    vars[joinVar('color-accent')] = theme.colors.accent;
    vars[joinVar('color-accent-foreground')] = theme.colors.accentForeground;
    // Design-system 1.1.0 semantic colors (FRE-813) — back the unprefixed
    // `--bg-elev`, `--surface`, `--surface-alt`, `--border-strong`, `--ring`,
    // `--fg-inverse` template tokens. See DS_11_BARE_ALIASES below for the
    // full bare↔canonical alias map.
    vars[joinVar('color-bg-elev')] = theme.colors.bgElev;
    vars[joinVar('color-surface-card')] = theme.colors.surfaceCard;
    vars[joinVar('color-surface-card-alt')] = theme.colors.surfaceCardAlt;
    vars[joinVar('color-border-strong')] = theme.colors.borderStrong;
    vars[joinVar('color-text-inverse')] = theme.colors.textInverse;
    vars[joinVar('color-text-strong')] = theme.colors.textStrong;
    vars[joinVar('color-text-quiet')] = theme.colors.textQuiet;
    vars[joinVar('color-ring-focus')] = theme.colors.ringFocus;
    // Category colors
    Object.entries(theme.category).forEach(([key, palette]) => {
        vars[joinVar(`color-category-${key}-primary`)] = palette.primary;
        vars[joinVar(`color-category-${key}-light`)] = palette.light;
        vars[joinVar(`color-category-${key}-dark`)] = palette.dark;
    });
    // Neighborhood colors (FRE-776 — CityAffinity ring grid)
    Object.entries(theme.neighborhood).forEach(([key, palette]) => {
        vars[joinVar(`color-neighborhood-${key}-primary`)] = palette.primary;
        vars[joinVar(`color-neighborhood-${key}-light`)] = palette.light;
        vars[joinVar(`color-neighborhood-${key}-dark`)] = palette.dark;
    });
    // Neutral scale
    Object.entries(theme.neutral).forEach(([key, value]) => {
        vars[joinVar(`color-neutral-${key}`)] = value;
    });
    // Spacing scale (semantic spacing uses same values for overlapping keys)
    Object.entries(theme.spacing).forEach(([key, value]) => {
        vars[joinVar(`spacing-${key}`)] = value;
    });
    // Radii — keep CSS var names kebab-case so `cardLg` → `--loci-radius-card-lg`
    // and `card2xl` → `--loci-radius-card-2xl` (matches the Style Dictionary
    // `loci/css-themed` output and CSS convention).
    Object.entries(theme.borderRadius).forEach(([key, value]) => {
        const kebab = key
            .replace(/([A-Z])/g, '-$1') // camelCase → kebab
            .replace(/([a-z])(\d)/g, '$1-$2') // letter→digit boundary
            .toLowerCase();
        vars[joinVar(`radius-${kebab}`)] = value;
    });
    // Shadows
    Object.entries(theme.shadows).forEach(([key, value]) => {
        vars[joinVar(`shadow-${key}`)] = value;
    });
    // Typography
    vars[joinVar('font-family-base')] = theme.typography.fontFamily;
    vars[joinVar('font-family-alt')] = theme.typography.fontFamilyAlt;
    vars[joinVar('font-family-mono')] = theme.typography.fontFamilyMono;
    if (theme.typography.fontFamilyDisplay) {
        vars[joinVar('font-family-display')] = theme.typography.fontFamilyDisplay;
    }
    Object.entries(theme.typography.fontSize).forEach(([key, value]) => {
        vars[joinVar(`font-size-${key}`)] = value;
    });
    Object.entries(theme.typography.lineHeights).forEach(([key, value]) => {
        vars[joinVar(`line-height-${key}`)] = String(value);
    });
    Object.entries(theme.typography.fontWeight).forEach(([key, value]) => {
        vars[joinVar(`font-weight-${key}`)] = String(value);
    });
    // Breakpoints
    Object.entries(theme.breakpoints).forEach(([key, value]) => {
        vars[joinVar(`breakpoint-${key}`)] = value;
    });
    // CLT (Charlotte) city accent tokens — theme-invariant per design source.
    // Emitted under the `--loci-*` namespace; the bare `--clt-*` aliases are
    // declared at `:root` in `apps/web/styles/tokens/color.css` for legacy direct
    // references (Tailwind config, organism inline styles).
    vars[joinVar('clt-skyline')] = cltAccentTokens.skyline;
    vars[joinVar('clt-skyline-light')] = cltAccentTokens.skylineLight;
    vars[joinVar('clt-skyline-deep')] = cltAccentTokens.skylineDeep;
    vars[joinVar('clt-skyline-glow')] = cltAccentTokens.skylineGlow;
    vars[joinVar('clt-skyline-wash')] = cltAccentTokens.skylineWash;
    vars[joinVar('clt-ember')] = cltAccentTokens.ember;
    vars[joinVar('clt-ember-light')] = cltAccentTokens.emberLight;
    // CLT accent-as-TEXT foreground tokens (FRE-930) — flip with theme mode.
    // The decorative `--loci-clt-*` accents above are illegible AS TEXT on light
    // surfaces (skyline-glow #4FC4C4 = 2.09:1, ember #E8833A = 2.71:1 on white).
    // These darken in light to clear WCAG 2.2 AA (≥4.5:1) and keep the original
    // bright accents in dark (which already pass on the deep-indigo grounds).
    // Names match the foreground tokens consumers already reference in
    // `apps/web/styles/tokens/color.css` (`--clt-skyline-glow-fg`, `--clt-ember-fg`,
    // `--clt-ember-deep`) so the promotion into the token SSoT is zero-diff there.
    const cltFg = theme.mode === 'dark' ? cltAccentForegroundTokensDark : cltAccentForegroundTokensLight;
    vars[joinVar('clt-skyline-glow-fg')] = cltFg.skylineGlowFg;
    vars[joinVar('clt-ember-fg')] = cltFg.emberFg;
    vars[joinVar('clt-ember-deep')] = cltFg.emberDeep;
    // Sunken background — flips with theme mode for the depth layer below `--bg`.
    vars[joinVar('bg-sunken')] =
        theme.mode === 'dark' ? sunkenBackgroundTokens.dark : sunkenBackgroundTokens.light;
    // ─── Design-system 1.1.0 bare-namespace aliases (FRE-813) ──────────────
    // Mirror DS_11_BARE_ALIASES so the runtime ThemeProvider sets these on
    // <html> in addition to the static `apps/web/styles/tokens/aliases.css` block.
    // Every entry is `var(--loci-*)` — no literal hex/px lives here.
    for (const [bare, canonical] of Object.entries(DS_11_BARE_ALIASES)) {
        vars[bare] = `var(${canonical})`;
    }
    return vars;
};
/**
 * FRE-813 — bare↔canonical alias map for design-system 1.1.0 template tokens.
 *
 * The two sanctioned namespaces:
 *   1. `--loci-*`     — canonical SSoT (declared once with literal values)
 *   2. unprefixed     — design-template handoff alias (thin `var(--loci-*)`)
 *
 * Every key is the bare CSS variable name (`--bg`, `--sp-6`, `--r-xl`, …).
 * Every value is the canonical CSS variable name it aliases.
 *
 * Generated (WEB-999): every `@runtime-alias` annotation in
 * `apps/web/design-system/tokens/tokens.css`. The same annotations emit the static CSS
 * aliases, so the runtime and stylesheet copies cannot disagree.
 *
 * Consumed by:
 *   - `buildCssVariableRecord()` above (runtime injector)
 *   - `packages/ui/style-dictionary.config.cjs` (build-time emission)
 *   - `packages/ui/src/theme/__tests__/tokens-pipeline.test.ts` (guardrails)
 *
 * Adding a new bare token? Declare the canonical in `tokens.css`, annotate it
 * with `@runtime-alias --bare`, and run `pnpm --filter web tokens:generate`.
 */
export const DS_11_BARE_ALIASES = Object.freeze({
    ...runtimeAliases,
});
/**
 * Serializes a theme to a CSS string with variables applied to the specified selector.
 * @param theme - The theme tokens to serialize
 * @param selector - The CSS selector to apply variables to (defaults to ':root')
 * @returns A complete CSS rule string with all theme variables
 */
export const toCssString = (theme, selector = ':root') => {
    const vars = buildCssVariableRecord(theme);
    const lines = Object.entries(vars).map(([key, value]) => `  ${key}: ${value};`);
    return `${selector} {\n${lines.join('\n')}\n}`;
};
/**
 * Tailwind `@theme` color roles consumed by `bg-background` / `text-foreground`.
 *
 * These are NOT DS 1.1.0 bare aliases (`--bg`, `--fg-1`). They live on `html`
 * via `@theme` as `var(--loci-*)`, but WEB-693's unlocked light FOUC rule can
 * pin them as hex. Re-declaring them as aliases here is the shared contract
 * so a Dark `data-theme` cannot leave light-on-light (or light-on-dark) text.
 */
export const TAILWIND_THEME_COLOR_ALIASES = Object.freeze({
    '--color-background': '--loci-color-background',
    '--color-foreground': '--loci-color-text',
});
/**
 * Applies theme CSS variables to the DOM by setting them on the target element.
 * @param mode - The theme mode ('light' or 'dark') to apply
 * @param options - Configuration options for applying the theme
 * @returns void
 */
export const applyThemeCssVariables = (mode, options = {}) => {
    if (typeof document === 'undefined')
        return;
    const theme = themeModes[mode];
    const target = options.target ?? document.documentElement;
    if (!target)
        return;
    const vars = buildCssVariableRecord(theme);
    Object.entries(vars).forEach(([key, value]) => {
        target.style.setProperty(key, value);
    });
    for (const [alias, canonical] of Object.entries(TAILWIND_THEME_COLOR_ALIASES)) {
        target.style.setProperty(alias, `var(${canonical})`);
    }
    // Beat the unlayered WEB-693 FOUC `background-color` / `color-scheme` on
    // `html:not([data-theme-source])` so the canvas matches `--bg` / `--fg-1`.
    target.style.setProperty('background-color', 'var(--bg)');
    target.style.setProperty('color', 'var(--fg-1)');
    target.style.setProperty('color-scheme', mode);
    if (options.setDataAttribute) {
        target.setAttribute('data-theme', mode);
    }
};
